import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import {
  ArrowLeft, Brain, BookOpen, Database, ChevronDown, ThumbsUp, ThumbsDown,
  Trash2, Search, ShieldAlert, Cpu, Quote, Link2Off, User, Zap, FileText,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getCountryFlag = (cc) => {
  if (!cc || cc === "XX" || cc === "LC") return "🌍";
  return String.fromCodePoint(...cc.toUpperCase().split("").map((c) => 127397 + c.charCodeAt()));
};

const SOURCE_BADGE = {
  knowledge_base: { label: "KB", cls: "bg-green-100 text-green-700" },
  blocked_no_kb: { label: "BLOCKED · NO KB", cls: "bg-red-100 text-red-700" },
  blocked_no_zoho: { label: "BLOCKED · NO ZOHO", cls: "bg-red-100 text-red-700" },
  blocked_no_client: { label: "BLOCKED · UNVERIFIED", cls: "bg-orange-100 text-orange-700" },
  blocked_guardrail: { label: "BLOCKED · GUARD RAIL", cls: "bg-rose-100 text-rose-700" },
  general_knowledge: { label: "GENERAL", cls: "bg-amber-100 text-amber-700" },
  cache: { label: "CACHE", cls: "bg-blue-100 text-blue-700" },
  system_template: { label: "TEMPLATE", cls: "bg-slate-100 text-slate-600" },
  booking: { label: "BOOKING", cls: "bg-teal-100 text-teal-700" },
};

const GROUNDING_LABEL = {
  kb_only: "Documents only (Knowledge Base)",
  zoho_only: "Zoho data only",
  open: "Open (no source restriction)",
  blocked_verification: "Blocked — client not verified",
  cache: "Served from cache",
  blocked_guardrail: "Blocked — guard rail",
  system_template: "System template reply",
  booking: "Booking flow",
};

const simCls = (s) =>
  s >= 0.7 ? "bg-green-100 text-green-700" : s >= 0.4 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700";

export default function ConversationDetail() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [conv, setConv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submittingFeedback, setSubmittingFeedback] = useState({});
  const [coachOpen, setCoachOpen] = useState(false);
  const [coachCategory, setCoachCategory] = useState("");
  const [coachContent, setCoachContent] = useState("");
  const [categories, setCategories] = useState([]);
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => { fetchConversation(); /* eslint-disable-next-line */ }, [sessionId]);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

  const fetchConversation = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ai/chat/conversation/${encodeURIComponent(sessionId)}`, authHeaders());
      setConv(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load conversation");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/admin/coaching-notes/categories`, authHeaders());
      setCategories(res.data.categories || []);
    } catch (e) { /* non-blocking */ }
  };

  const submitFeedback = async (msg, type) => {
    if (!msg.id) return toast.error("Message ID missing");
    const key = msg.id;
    try {
      setSubmittingFeedback((p) => ({ ...p, [key]: type }));
      await axios.post(`${API}/public/chat/feedback`, {
        session_id: conv.session_id, message_id: msg.id, actor_id: conv.actor_id,
        feedback_type: type, feedback_comment: `Admin review: ${type === "positive" ? "Good response" : "Needs improvement"}`,
      }, authHeaders());
      toast.success(`Admin feedback: ${type === "positive" ? "👍" : "👎"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit feedback");
      setSubmittingFeedback((p) => { const n = { ...p }; delete n[key]; return n; });
    }
  };

  const openCoach = () => { setCoachOpen(true); fetchCategories(); };

  const submitCoachingNote = async () => {
    if (!coachCategory.trim() || !coachContent.trim()) return;
    setSavingNote(true);
    try {
      await axios.post(`${API}/admin/ai-actors/${conv.actor_id}/coaching-notes`, {
        category: coachCategory, content: coachContent, conversation_id: conv.session_id,
      }, authHeaders());
      toast.success("Coaching note added! AI Actor will learn from this.");
      setCoachOpen(false); setCoachContent(""); setCoachCategory("");
    } catch (e) {
      toast.error("Failed to add coaching note");
    } finally { setSavingNote(false); }
  };

  const unlinkZoho = async () => {
    if (!window.confirm("Unlink this Zoho session? The client will need to re-identify next time.")) return;
    try {
      await axios.delete(`${API}/zoho/sessions/${encodeURIComponent(conv.session_id)}`, authHeaders());
      toast.success("Zoho session unlinked");
      setConv((p) => ({ ...p, zoho_client: null }));
    } catch (e) { toast.error("Failed to unlink session"); }
  };

  const deleteConversation = async () => {
    if (!window.confirm("Delete this conversation permanently? This removes all messages, feedback, coaching notes, and Zoho session data. This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/chat-conversations/${encodeURIComponent(conv.session_id)}`, authHeaders());
      toast.success("Conversation deleted");
      navigate("/chat-moderation");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete conversation"); }
  };

  const deleteDisabled = () => {
    if (!conv?.last_message_at) return false;
    return (Date.now() - new Date(conv.last_message_at).getTime()) / 1000 < 300;
  };

  if (loading) {
    return <div className="p-8 text-center text-muted-foreground" data-testid="conversation-loading">Loading conversation…</div>;
  }
  if (!conv) {
    return (
      <div className="p-8 text-center">
        <p className="text-muted-foreground mb-4">Conversation not found.</p>
        <Button variant="outline" onClick={() => navigate("/chat-moderation")} data-testid="back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-5" data-testid="conversation-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/chat-moderation")} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Chat Moderation
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={openCoach} data-testid="add-coaching-note-btn">
            <BookOpen className="w-4 h-4 mr-1" /> Add coaching note
          </Button>
          <Button variant="destructive" size="sm" onClick={deleteConversation} disabled={deleteDisabled()}
            title={deleteDisabled() ? "Cannot delete active conversation. Wait 5 minutes." : "Delete this conversation permanently"}
            data-testid="delete-conversation-btn">
            <Trash2 className="w-4 h-4 mr-1" /> Delete
          </Button>
        </div>
      </div>

      {/* Conversation summary */}
      <Card data-testid="conversation-summary">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <h1 className="text-lg font-semibold text-slate-900">{conv.actor_name}</h1>
            <Badge variant="outline" className="capitalize">{conv.type}</Badge>
            {conv.zoho_client?.confirmed && (
              <Badge variant="outline" className="bg-cyan-50 text-cyan-700 border-cyan-200">Zoho ✓ {conv.zoho_client.account_name}</Badge>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><p className="text-xs text-muted-foreground">User</p><p className="font-medium flex items-center gap-1"><User className="w-3 h-3" />{conv.user_name}</p></div>
            <div><p className="text-xs text-muted-foreground">Email / Phone</p><p className="font-medium truncate">{conv.user_email}</p></div>
            <div><p className="text-xs text-muted-foreground">Country</p><p className="font-medium">{getCountryFlag(conv.country_code)} {conv.country}</p></div>
            <div><p className="text-xs text-muted-foreground">Messages</p><p className="font-medium">{conv.message_count}</p></div>
          </div>
          {conv.zoho_client && (
            <div className="mt-3 flex items-center justify-between bg-cyan-50 border border-cyan-200 rounded p-2">
              <p className="text-xs text-cyan-800">Identified via <strong>{conv.zoho_client.identifier_used || "—"}</strong> · {conv.zoho_client.confirmed ? "Confirmed" : "Pending"}</p>
              <Button variant="outline" size="sm" className="h-7 text-xs text-red-500 hover:text-red-700" onClick={unlinkZoho} data-testid="unlink-zoho-btn">
                <Link2Off className="w-3 h-3 mr-1" /> Unlink Zoho
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Messages */}
      <div className="space-y-4" data-testid="messages-list">
        {conv.messages.map((msg, i) => (
          <div key={msg.id || i} className="space-y-2">
            {msg.user_message && (
              <div className="flex justify-end">
                <div className="max-w-[75%] rounded-lg px-4 py-2 bg-slate-900 text-white">
                  <p className="text-[11px] text-slate-300 mb-1">{conv.user_name}</p>
                  <div className="text-sm prose prose-sm prose-invert max-w-none"><ReactMarkdown>{msg.user_message}</ReactMarkdown></div>
                  <p className="text-[10px] text-slate-400 mt-1">{new Date(msg.timestamp).toLocaleString()}</p>
                </div>
              </div>
            )}
            {msg.assistant_message && (
              <div className="flex justify-start">
                <div className="max-w-[85%] w-full rounded-lg px-4 py-3 bg-white border border-slate-200" data-testid={`ai-message-${i}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-xs text-muted-foreground">{conv.actor_name}</p>
                    {(() => {
                      const src = msg.audit?.grounding?.response_source || msg.response_source;
                      const b = SOURCE_BADGE[src];
                      return b ? <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${b.cls}`} data-testid={`msg-source-badge-${i}`}>{b.label}</span> : null;
                    })()}
                  </div>
                  <div className="text-sm prose prose-sm max-w-none"><ReactMarkdown>{msg.assistant_message}</ReactMarkdown></div>

                  <div className="flex items-center justify-between mt-2">
                    <p className="text-[10px] text-muted-foreground">{new Date(msg.timestamp).toLocaleString()}</p>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className={`h-7 px-2 ${submittingFeedback[msg.id] === "positive" ? "text-green-600" : "text-gray-500 hover:text-green-600"}`} onClick={() => submitFeedback(msg, "positive")} data-testid={`feedback-up-${i}`}><ThumbsUp className="w-3 h-3" /></Button>
                      <Button variant="ghost" size="sm" className={`h-7 px-2 ${submittingFeedback[msg.id] === "negative" ? "text-red-600" : "text-gray-500 hover:text-red-600"}`} onClick={() => submitFeedback(msg, "negative")} data-testid={`feedback-down-${i}`}><ThumbsDown className="w-3 h-3" /></Button>
                    </div>
                  </div>

                  {/* Audit trail */}
                  <AuditPanel audit={msg.audit} index={i} />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Coaching note dialog */}
      <Dialog open={coachOpen} onOpenChange={setCoachOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BookOpen className="w-5 h-5 text-indigo-600" /> Add Coaching Note to AI Actor</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-sm text-indigo-900">
              💡 Based on this conversation, guide the AI Actor's future responses. Notes are added to the AI's system prompt for all future chats.
            </div>
            <div>
              <Label>Category</Label>
              <Input value={coachCategory} onChange={(e) => setCoachCategory(e.target.value)} placeholder="e.g., Pricing, Communication…" list="cd-cats" className="mt-1" data-testid="coaching-category-input" />
              <datalist id="cd-cats">{categories.map((c, i) => <option key={i} value={c} />)}</datalist>
            </div>
            <div>
              <Label>Coaching Note</Label>
              <Textarea value={coachContent} onChange={(e) => setCoachContent(e.target.value)} rows={5} placeholder="Be specific and actionable…" className="mt-1" data-testid="coaching-content-input" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setCoachOpen(false)} disabled={savingNote}>Cancel</Button>
              <Button onClick={submitCoachingNote} disabled={savingNote || !coachContent.trim() || !coachCategory.trim()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-coaching-note-btn">{savingNote ? "Adding…" : "Add Coaching Note"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AuditPanel({ audit, index }) {
  if (!audit) {
    return (
      <div className="mt-2 pt-2 border-t border-slate-100">
        <p className="text-[10px] text-slate-400 italic">No audit data (legacy message).</p>
      </div>
    );
  }
  const t = audit.thinking || {};
  const g = audit.grounding || {};
  const kb = audit.kb_trigger || [];
  const zoho = audit.zoho_trigger;
  const cache = audit.cache;
  const guardrail = audit.guardrail;
  const template = audit.template;
  const src = g.response_source;

  return (
    <Collapsible className="mt-2 pt-2 border-t border-slate-100">
      <CollapsibleTrigger className="flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:text-indigo-800" data-testid={`audit-toggle-${index}`}>
        <ChevronDown className="w-3 h-3" /> Why this response?
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2 space-y-2" data-testid={`audit-panel-${index}`}>
        {/* Served from cache */}
        {src === "cache" && (
          <div className="rounded-md bg-blue-50/70 border border-blue-200 p-2" data-testid={`audit-cache-${index}`}>
            <p className="text-[10px] font-semibold text-blue-800 flex items-center gap-1 mb-1"><Zap className="w-3 h-3" /> Served from cache</p>
            <p className="text-[10px] text-blue-700">This answer was reused from a previous, near-identical question — it was not freshly generated.</p>
            {cache?.cached_at && <p className="text-[10px] text-blue-500 mt-1">Originally generated: {new Date(cache.cached_at).toLocaleString()}</p>}
            {cache?.original_query && <p className="text-[10px] text-blue-500 italic mt-0.5">Original question: “{cache.original_query}”</p>}
          </div>
        )}

        {/* Blocked by guard rail */}
        {src === "blocked_guardrail" && (
          <div className="rounded-md bg-rose-50/70 border border-rose-200 p-2" data-testid={`audit-guardrail-${index}`}>
            <p className="text-[10px] font-semibold text-rose-800 flex items-center gap-1 mb-1"><ShieldAlert className="w-3 h-3" /> Blocked by guard rail</p>
            <p className="text-[10px] text-rose-700">{guardrail?.reason || "The question was judged outside this actor's profile/specialty."}</p>
            {guardrail?.strictness && <p className="text-[10px] text-rose-500 mt-1">Strictness: {guardrail.strictness}</p>}
          </div>
        )}

        {/* System template / booking reply */}
        {(src === "system_template" || src === "booking") && (
          <div className="rounded-md bg-slate-50 border border-slate-200 p-2" data-testid={`audit-template-${index}`}>
            <p className="text-[10px] font-semibold text-slate-700 flex items-center gap-1 mb-1"><FileText className="w-3 h-3" /> {src === "booking" ? "Booking flow" : "System template reply"}</p>
            <p className="text-[10px] text-slate-500">{src === "booking" ? "Generated by the appointment booking flow (not the knowledge base or general model)." : "A predefined system/Zoho template reply — not generated from the knowledge base or general model."}</p>
            {template?.kind && <p className="text-[10px] text-slate-400 mt-1">Type: {template.kind}</p>}
          </div>
        )}

        {/* Thinking layer (only when a thinking-layer decision was made) */}
        {audit.thinking && (
        <div className="rounded-md bg-slate-50 border border-slate-200 p-2">
          <p className="text-[10px] font-semibold text-slate-700 flex items-center gap-1 mb-1"><Brain className="w-3 h-3 text-indigo-500" /> Thinking Layer</p>
          <div className="flex flex-wrap items-center gap-1 text-[10px]">
            {t.matched_intent_id && <Badge variant="outline" className="text-[9px]">{t.matched_intent_id}</Badge>}
            {t.action && <><span className="text-slate-400">→</span><Badge variant="outline" className="text-[9px] bg-indigo-50 text-indigo-700">{t.action}</Badge></>}
            {t.grounding && <Badge variant="outline" className="text-[9px] bg-blue-50 text-blue-700">{GROUNDING_LABEL[t.grounding] || t.grounding}</Badge>}
            {t.confidence && <Badge variant="outline" className="text-[9px]">conf: {t.confidence}</Badge>}
          </div>
          {t.reasoning && <p className="text-[10px] text-slate-500 italic mt-1 flex gap-1"><Quote className="w-3 h-3 shrink-0" />{t.reasoning}</p>}
        </div>
        )}

        {/* KB trigger */}
        {kb.length > 0 && (
          <div className="rounded-md bg-green-50/60 border border-green-200 p-2" data-testid={`audit-kb-${index}`}>
            <p className="text-[10px] font-semibold text-green-800 flex items-center gap-1 mb-1"><BookOpen className="w-3 h-3" /> Knowledge Base trigger ({kb.length})</p>
            <div className="space-y-1">
              {kb.map((c, ci) => (
                <div key={ci} className="bg-white rounded border border-green-100 px-2 py-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-medium text-slate-700 truncate">{c.doc_title || c.source_name}</span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${simCls(c.similarity)}`}>{(c.similarity * 100).toFixed(0)}%</span>
                  </div>
                  {c.content_snippet && <p className="text-[10px] text-slate-500 mt-1 line-clamp-3">“{c.content_snippet}”</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Zoho trigger (privacy-safe: field names + ticket numbers only) */}
        {zoho?.injected && (
          <div className="rounded-md bg-purple-50/60 border border-purple-200 p-2" data-testid={`audit-zoho-${index}`}>
            <p className="text-[10px] font-semibold text-purple-800 flex items-center gap-1 mb-1"><Database className="w-3 h-3" /> Zoho trigger</p>
            {zoho.fields?.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-1">
                {zoho.fields.map((f, fi) => <Badge key={fi} variant="outline" className="text-[9px] bg-white text-purple-700 border-purple-200">{f}</Badge>)}
              </div>
            )}
            {zoho.ticket_numbers?.length > 0 && (
              <p className="text-[10px] text-purple-700">Tickets: {zoho.ticket_numbers.map((n) => `#${n}`).join(", ")}</p>
            )}
            <p className="text-[9px] text-purple-400 mt-1">Live-fetched · field names only · no customer values stored.</p>
          </div>
        )}

        {/* Blocked / no-match note */}
        {(g.response_source === "blocked_no_kb" || g.response_source === "blocked_no_zoho" || g.response_source === "blocked_no_client") && (
          <div className="rounded-md bg-red-50/60 border border-red-200 p-2 text-[10px] text-red-700 flex items-center gap-1" data-testid={`audit-blocked-${index}`}>
            <ShieldAlert className="w-3 h-3 shrink-0" />
            {g.response_source === "blocked_no_kb" && <span>No document scored ≥ {(g.threshold ?? 0).toFixed?.(2) ?? g.threshold} (top match {((g.top_similarity || 0) * 100).toFixed(0)}%) → refused with fallback.</span>}
            {g.response_source === "blocked_no_zoho" && <span>No matching Zoho data for this client → refused with fallback.</span>}
            {g.response_source === "blocked_no_client" && <span>Client not verified → asked for identification before answering.</span>}
          </div>
        )}

        {/* Model */}
        <div className="text-[10px] text-slate-500 flex items-center gap-2">
          <span className="flex items-center gap-1"><Cpu className="w-3 h-3" /> {audit.model_used || g.response_source || "—"}</span>
          {typeof audit.fallback_level === "number" && <span>· fallback level {audit.fallback_level}</span>}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
